import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  einvoiceApi,
  invoiceLabel,
  statusText,
  type EInvoiceConfigView,
  type EInvoiceSource,
  type EInvoiceView,
} from '../lib/einvoice'
import { kvSet } from '../lib/db'
import { useApp } from '../lib/store'
import { AppBar } from '../components/AppBar'

/**
 * Cài đặt → Hoá đơn điện tử.
 *
 * Màn hình này quyết định quán có xuất hoá đơn hay không, nên nó phải nói thẳng thứ chủ
 * quán đang thiếu chứ đừng để họ bật lên rồi mai mốt mới biết chưa có tờ nào lên tới cơ
 * quan thuế. Mọi lỗi cấu hình đều chặn ngay lúc lưu, kèm câu chỉ rõ phải đi đâu sửa.
 */
export function EInvoiceSettingsScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const shop = useApp((s) => s.shop)

  const [cfg, setCfg] = useState<EInvoiceConfigView | null>(null)
  const [failed, setFailed] = useState<EInvoiceView[]>([])
  const [busy, setBusy] = useState(false)

  const [enabled, setEnabled] = useState(false)
  const [source, setSource] = useState<EInvoiceSource>('System')
  const [provider, setProvider] = useState('')
  const [account, setAccount] = useState('')
  const [secret, setSecret] = useState('')
  const [endpoint, setEndpoint] = useState('')
  const [templateCode, setTemplateCode] = useState('')
  const [series, setSeries] = useState('')
  const [autoIssue, setAutoIssue] = useState(true)

  function apply(next: EInvoiceConfigView) {
    setCfg(next)
    setEnabled(next.enabled)
    setSource(next.source)
    setProvider(next.provider ?? next.providers[0]?.key ?? '')
    setAccount(next.account ?? '')
    setSecret('')
    setEndpoint(next.endpoint ?? '')
    setTemplateCode(next.templateCode ?? '')
    setSeries(next.series ?? '')
    setAutoIssue(next.autoIssue)
  }

  useEffect(() => {
    void (async () => {
      try {
        apply(await einvoiceApi.config())
      } catch (e) {
        say(e instanceof Error ? e.message : 'Không đọc được cấu hình')
      }
    })()
    // Chỉ nạp một lần lúc mở màn hình; say không đổi giữa các lần vẽ.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!cfg?.failedCount) {
      setFailed([])
      return
    }
    void einvoiceApi
      .list(undefined, 'Failed')
      .then(setFailed)
      .catch(() => setFailed([]))
  }, [cfg?.failedCount])

  async function save() {
    setBusy(true)
    try {
      const next = await einvoiceApi.saveConfig({
        enabled,
        source,
        provider: source === 'Own' ? provider : null,
        account: source === 'Own' ? account.trim() || null : null,
        secret: source === 'Own' ? secret.trim() || null : null,
        endpoint: source === 'Own' ? endpoint.trim() || null : null,
        templateCode: templateCode.trim() || null,
        series: series.trim().toUpperCase() || null,
        autoIssue,
      })
      apply(next)

      // Người bán đọc hai cờ này để biết có hiện nút "Khách lấy hoá đơn" hay không,
      // nên cập nhật ngay chứ đừng đợi lần kéo danh mục sau.
      if (shop) {
        const merged = { ...shop, eInvoiceEnabled: next.enabled, eInvoiceAutoIssue: next.autoIssue }
        await kvSet('shop', merged)
        useApp.setState({ shop: merged })
      }

      say(next.enabled ? 'Đã bật hoá đơn điện tử' : 'Đã tắt hoá đơn điện tử')
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa lưu được')
    }
    setBusy(false)
  }

  async function retry(orderId: string) {
    try {
      const res = await einvoiceApi.retry(orderId)
      say(res.status === 'Issued' ? `Đã xuất ${invoiceLabel(res) ?? 'hoá đơn'}` : (res.error ?? 'Vẫn chưa xuất được'))
      apply(await einvoiceApi.config())
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa phát hành lại được')
    }
  }

  if (!cfg) return <div className="screen" />

  return (
    <div className="screen">
      <AppBar title="Hoá đơn điện tử" onBack={() => nav('/settings')} />

      <div className="body">
        <p className="hint">
          Hoá đơn khởi tạo từ máy tính tiền, gửi lên cơ quan thuế qua nhà cung cấp có giấy phép.
          Hộ kinh doanh ngành ăn uống và bán lẻ có doanh thu từ 1 tỷ/năm thuộc diện bắt buộc.
        </p>

        {cfg.warning && (
          <div className="card warn-card">
            <p className="note">{cfg.warning}</p>
          </div>
        )}

        <label className="row-btn" style={{ cursor: 'pointer' }}>
          <span className="row-icon" aria-hidden="true">
            🧾
          </span>
          <span className="row-text">
            <b>Xuất hoá đơn điện tử</b>
            <small>{enabled ? 'Đang bật' : 'Đang tắt — bán hàng vẫn in bill bình thường'}</small>
          </span>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        </label>

        {enabled && (
          <>
            <h2 className="section-label">Tài khoản</h2>
            <div className="seg">
              <button
                type="button"
                aria-pressed={source === 'System'}
                disabled={!cfg.systemReady}
                onClick={() => setSource('System')}
              >
                Qua sigoPOS
              </button>
              <button type="button" aria-pressed={source === 'Own'} onClick={() => setSource('Own')}>
                Của quán
              </button>
            </div>

            <p className="note">
              {source === 'System'
                ? cfg.systemReady
                  ? `Dùng hợp đồng của sigoPOS với ${cfg.systemProvider}. Quán không phải ký thêm hợp đồng nào.`
                  : 'sigoPOS chưa mở dịch vụ này. Gọi cho sigoPOS hoặc dùng hợp đồng sẵn có của quán.'
                : 'Quán đã ký hợp đồng riêng với nhà cung cấp. Khai tài khoản họ cấp vào đây.'}
            </p>

            {source === 'Own' && (
              <>
                <label className="field">
                  <span>Nhà cung cấp</span>
                  <select value={provider} onChange={(e) => setProvider(e.target.value)}>
                    {cfg.providers.map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="field-row">
                  <label className="field">
                    <span>Tên đăng nhập</span>
                    <input
                      value={account}
                      onChange={(e) => setAccount(e.target.value)}
                      autoComplete="off"
                      maxLength={120}
                    />
                  </label>

                  <label className="field">
                    <span>Mật khẩu</span>
                    <input
                      type="password"
                      value={secret}
                      onChange={(e) => setSecret(e.target.value)}
                      placeholder={cfg.hasSecret ? '••••••  đã lưu' : ''}
                      autoComplete="new-password"
                    />
                  </label>
                </div>

                <label className="field">
                  <span>Địa chỉ máy chủ (nếu nhà cung cấp cấp riêng)</span>
                  <input
                    value={endpoint}
                    onChange={(e) => setEndpoint(e.target.value)}
                    placeholder="https://…"
                    maxLength={250}
                  />
                </label>
              </>
            )}

            <h2 className="section-label">Ký hiệu hoá đơn</h2>
            <div className="field-row">
              <label className="field">
                <span>Mẫu số</span>
                <input
                  value={templateCode}
                  onChange={(e) => setTemplateCode(e.target.value)}
                  placeholder="2"
                  maxLength={20}
                />
              </label>

              <label className="field">
                <span>Ký hiệu</span>
                <input
                  value={series}
                  onChange={(e) => setSeries(e.target.value.toUpperCase())}
                  placeholder="C25MAA"
                  maxLength={8}
                  style={{ textTransform: 'uppercase' }}
                />
              </label>
            </div>
            <p className="note">
              Hai thứ này do nhà cung cấp cấp khi mở dịch vụ, không tự đặt được. Ký tự thứ tư của
              ký hiệu phải là <b>M</b> — đó là dấu hiệu hoá đơn khởi tạo từ máy tính tiền.
            </p>

            <h2 className="section-label">Khi nào xuất</h2>
            <div className="seg">
              <button type="button" aria-pressed={autoIssue} onClick={() => setAutoIssue(true)}>
                Mọi đơn
              </button>
              <button type="button" aria-pressed={!autoIssue} onClick={() => setAutoIssue(false)}>
                Khi khách xin
              </button>
            </div>
            <p className="note">
              {autoIssue
                ? 'Bán xong là tự xuất. Hộ thuộc diện bắt buộc phải lập hoá đơn cho mọi lần bán, nên đây là cách đúng.'
                : 'Chỉ xuất khi người bán bấm nút ở màn hình thu tiền. Chọn cách này thì tự chịu trách nhiệm với những đơn không xuất.'}
            </p>
          </>
        )}

        {(cfg.pendingCount > 0 || cfg.failedCount > 0 || cfg.lastOkAt) && (
          <>
            <h2 className="section-label">Tình hình</h2>
            <div className="card">
              <div className="pay-row">
                <span>Đang chờ xuất</span>
                <b className="num">{cfg.pendingCount}</b>
              </div>
              <div className="pay-row">
                <span>Chưa xuất được</span>
                <b className="num">{cfg.failedCount}</b>
              </div>
              {cfg.lastOkAt && (
                <p className="note">
                  Tờ gần nhất xuất lúc {new Date(cfg.lastOkAt).toLocaleString('vi-VN')}.
                </p>
              )}
              {cfg.lastError && <p className="note">{cfg.lastError}</p>}
            </div>
          </>
        )}

        {failed.length > 0 && (
          <div className="list">
            {failed.map((e) => (
              <div key={e.orderId} className="row-btn" style={{ cursor: 'default' }}>
                <span className="row-icon" aria-hidden="true">
                  ⚠️
                </span>
                <span className="row-text">
                  <b>{e.orderCode}</b>
                  <small>{e.error ?? statusText[e.status]}</small>
                </span>
                <button type="button" className="btn btn-ghost" onClick={() => void retry(e.orderId)}>
                  Xuất lại
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="dock">
        <button type="button" className="btn btn-brand btn-lg grow" onClick={() => void save()} disabled={busy}>
          {busy ? 'Đang lưu…' : 'Lưu'}
        </button>
      </div>
    </div>
  )
}
